// =============================================================================
// app/src/paginas/ContasPagarSalario.tsx — a linha de salário em Contas a pagar.
//
// Pedido do dono (08/10/2026): o salário dentro de Contas a pagar. A linha é a
// MESMA da folha (payroll_entries) — não uma cópia em contas_pagar —, então
// pagar aqui é pagar no Painel, e vice-versa. Ver o comentário de
// salarioNaLista em lib/contasPagar.ts e a migração 094.
//
// O que a linha de salário NÃO tem, de propósito, e o porquê de cada um:
//   - checkbox do lote: o lote é pagar_contas, uma transação que só conhece
//     contas_pagar. Misturar salário ali dividiria o "tudo ou nada" em dois;
//   - anexo e código de barras: salário não tem boleto, e o comprovante do
//     pix mora no banco do dono;
//   - editar e excluir: comissão, bônus, desconto e o próprio lançamento são
//     do Painel, que já sabe fazer. O link "ajustar no Painel" leva até lá.
//
// E o que ela não PAGA (revisão da 094, 09/10/2026): a linha prevista nasce
// com comissão zero. De quem tem comissão no combinado, ela vale só o
// salário-base — pagar daqui pagaria menos do que a pessoa tem a receber. Essa
// linha mostra "falta a comissão" e manda conferir no Painel; o banco recusa o
// mesmo (pagar_folha), para o caso de a tela não saber ainda.
// =============================================================================
import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PaymentMethod } from '@core/types';
import Aviso from '../componentes/Aviso';
import Segmentado from '../componentes/Segmentado';
import { desfazerPagamentoFolha, pagarFolha } from '../lib/consultas';
import type { SalarioNaLista } from '../lib/contasPagar';
import { brl, dataSoDiaBR } from '../lib/formato';
import { mensagemDeErro } from '../lib/supabase';

/** As formas que pagam salário — as mesmas do Painel. */
export const FORMAS_DO_SALARIO: ReadonlyArray<{ id: PaymentMethod; rotulo: string }> = [
  { id: 'pix', rotulo: 'PIX' },
  { id: 'transferencia', rotulo: 'Transferência' },
  { id: 'dinheiro', rotulo: 'Dinheiro' },
  { id: 'boleto', rotulo: 'Boleto' },
];

/** O pagamento sai pela MESMA RPC do Painel (pagar_folha): uma linha, um
 *  lançamento no caixa, os dois lugares mostram pago. */
export const pagamentoDoSalario = (s: Pick<SalarioNaLista, 'id'>, forma: PaymentMethod, data: string) =>
  pagarFolha(s.id, forma, data);

/** "10/2026" — de qual mês é o salário (a competência), que pode não ser o
 *  mês em que ele vence. */
const mesDaFolha = (competencia: string) => `${competencia.slice(5, 7)}/${competencia.slice(0, 4)}`;

export function LinhaDeSalario({ salario, aba, hoje }: {
  salario: SalarioNaLista;
  aba: 'abertas' | 'pagas';
  hoje: string;
}) {
  const cliente = useQueryClient();
  const [pagando, setPagando] = useState(false);
  const [desfazendo, setDesfazendo] = useState(false);

  const recarregar = () => {
    // A mesma linha aparece em Contas a pagar, no Painel e no fluxo; a baixa
    // lança (ou apaga) a saída no caixa.
    for (const chave of [['contas-pagar'], ['folha-contas'], ['folha'], ['caixa'], ['fluxo']]) {
      void cliente.invalidateQueries({ queryKey: chave });
    }
  };

  const pagar = useMutation({
    mutationFn: (p: { forma: PaymentMethod; data: string }) => pagamentoDoSalario(salario, p.forma, p.data),
    onSuccess: () => { setPagando(false); recarregar(); },
  });
  const desfazer = useMutation({
    mutationFn: () => desfazerPagamentoFolha(salario.id),
    onSuccess: () => { setDesfazendo(false); recarregar(); },
  });

  const pago = Boolean(salario.pago_em);
  const atrasada = !pago && salario.vencimento < hoje;

  return (
    <li className={`cartao flex flex-wrap items-center justify-between gap-3 p-3 ${
      atrasada ? 'border-erro/50' : ''}`}>
      {/* O lugar do checkbox do lote, vazio: o nome alinha com o das contas. */}
      {aba === 'abertas' && <span className="size-4 shrink-0" aria-hidden="true" />}
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-bold">{salario.descricao}</span>
        <span className="block text-xs text-mute">
          Salário · folha de {mesDaFolha(salario.competencia)} · vence {dataSoDiaBR(salario.vencimento)}
          {salario.prevista && !pago ? ' · pelo combinado' : ''}
          {salario.falta_comissao && !pago ? ' · falta a comissão do mês' : ''}
          {atrasada ? ' · ATRASADA' : ''}
          {salario.pago_em ? ` · pago em ${dataSoDiaBR(salario.pago_em)}` : ''}
          {pago && salario.forma_pagamento ? ` · ${salario.forma_pagamento}` : ''}
        </span>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-3 gap-y-1">
        <span className="tabular-nums text-lg font-black">{brl(salario.valor_real)}</span>
        {pago ? (
          <button type="button" className="text-xs text-dim underline"
                  onClick={() => setDesfazendo(!desfazendo)}>
            desfazer
          </button>
        ) : (
          <>
            {/* Comissão, bônus e desconto se mexem lá — a linha é a mesma. */}
            <Link to="/painel" className="text-xs text-dim underline">ajustar no Painel</Link>
            <button type="button" className="botao-primario px-3 py-1 text-xs"
                    onClick={() => setPagando(!pagando)}>Pagar</button>
          </>
        )}
      </div>
      {pagando && !pago && (
        <PainelPagarSalario salario={salario} hoje={hoje}
                            pendente={pagar.isPending} erro={pagar.error}
                            aoFechar={() => setPagando(false)}
                            aoPagar={(forma, data) => pagar.mutate({ forma, data })} />
      )}
      {desfazendo && pago && (
        <ConfirmarDesfazerSalario pendente={desfazer.isPending} erro={desfazer.error}
                                  aoVoltar={() => setDesfazendo(false)}
                                  aoDesfazer={() => desfazer.mutate()} />
      )}
    </li>
  );
}

/** O painel do "Pagar": a forma e o dia. O valor não se digita — é o da
 *  folha; quem muda o valor é o Painel. Data futura não: o banco recusa, e a
 *  tela não deixa nem escolher (`max`). */
export function PainelPagarSalario({ salario, hoje, pendente, erro, aoFechar, aoPagar }: {
  salario: SalarioNaLista;
  hoje: string;
  pendente: boolean;
  erro: unknown;
  aoFechar: () => void;
  aoPagar: (forma: PaymentMethod, data: string) => void;
}) {
  const [data, setData] = useState(hoje);
  const [forma, setForma] = useState<PaymentMethod>('pix');
  const futura = data > hoje;
  const idData = `sal-data-${salario.id}`;

  return (
    <div className="mt-2 w-full space-y-3 border border-edge p-3">
      <p className="text-xs text-mute">
        Sai do caixa como <strong>salário</strong>, no valor da folha. Comissão,
        bônus e desconto se ajustam no Painel antes de pagar.
      </p>
      {salario.falta_comissao && (
        <Aviso tipo="alerta">
          Este valor é só o salário-base: a comissão do mês ainda não entrou. Confira
          a comissão no <Link to="/painel" className="underline">Painel</Link> e salve —
          aí o salário volta aqui com o valor certo para pagar.
        </Aviso>
      )}
      <div>
        <p className="rotulo">Como pagou</p>
        <Segmentado<PaymentMethod> itens={FORMAS_DO_SALARIO} valor={forma} aoMudar={setForma} />
      </div>
      <div>
        <label className="rotulo" htmlFor={idData}>Quando pagou</label>
        <input id={idData} type="date" className="campo w-auto" value={data} max={hoje}
               onChange={(e) => setData(e.target.value)} />
        {data !== hoje && !futura && (
          <p className="mt-1 text-xs text-dim">A saída cai no caixa do dia escolhido, não no de hoje.</p>
        )}
        {futura && <p className="mt-1 text-xs text-erro">A data do pagamento não pode estar no futuro.</p>}
      </div>
      {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-1.5 text-xs" onClick={aoFechar}>
          Voltar
        </button>
        <button type="button" className="botao-primario flex-1 py-1.5 text-xs disabled:opacity-50"
                disabled={pendente || futura || !data || salario.falta_comissao}
                onClick={() => aoPagar(forma, data)}>
          {pendente ? 'Lançando…' : `Pagar ${brl(salario.valor_real)}`}
        </button>
      </div>
    </div>
  );
}

/** Desfazer APAGA a saída do caixa: 2 passos, como o desfazer das contas. */
export function ConfirmarDesfazerSalario({ pendente, erro, aoVoltar, aoDesfazer }: {
  pendente: boolean;
  erro: unknown;
  aoVoltar: () => void;
  aoDesfazer: () => void;
}) {
  return (
    <div className="mt-2 w-full space-y-2 border border-erro/40 bg-erro/5 p-3">
      <p className="text-xs text-mute">
        Apaga a saída do caixa e devolve o salário para "A pagar" — aqui e no Painel.
      </p>
      {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}
      <div className="flex gap-2">
        <button type="button" className="botao-secundario flex-1 py-1.5 text-xs" onClick={aoVoltar}>
          Voltar
        </button>
        <button type="button" className="botao-perigo flex-1 py-1.5 text-xs"
                disabled={pendente} onClick={aoDesfazer}>
          Desfazer o pagamento
        </button>
      </div>
    </div>
  );
}
